// Cross-tenant search modal for the Super Admin shell. Mirrors the
// client-side `GlobalSearch` UX (Cmd+K, debounced query, grouped
// results, keyboard navigation, recent searches) but hits the
// `/super/search` endpoint which returns three categories: clients,
// candidates, assessments.
import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Search,
  Building2,
  Ticket,
  Receipt,
  Loader2,
  CornerDownLeft,
  Clock,
} from "lucide-react"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { api } from "@/lib/api"
import { cn } from "@/lib/utils"

interface SuperSearchResult {
  type: "client" | "ticket" | "invoice"
  id: string
  title: string
  subtitle: string
  link: string
}

interface SearchResponse {
  clients: {
    id: string
    name: string
    email: string
    companyName: string | null
  }[]
  tickets: {
    id: string
    subject: string
    status: string
    priority: string
    clientName: string | null
    clientId: string | null
  }[]
  invoices: {
    id: string
    invoiceNumber: string
    amount: number
    status: string
    clientName: string | null
    clientId: string | null
  }[]
}

const TYPE_META: Record<
  SuperSearchResult["type"],
  { icon: typeof Search; section: string; color: string }
> = {
  client: { icon: Building2, section: "Clients", color: "text-indigo-600" },
  ticket: { icon: Ticket, section: "Support Tickets", color: "text-amber-600" },
  invoice: { icon: Receipt, section: "Invoices", color: "text-emerald-600" },
}

const RECENTS_KEY = "yoma.super.search.recents"

function readRecents(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(RECENTS_KEY) || "[]")
    return Array.isArray(v) ? v.slice(0, 5) : []
  } catch {
    return []
  }
}
function pushRecent(q: string) {
  if (!q.trim()) return
  try {
    const next = [q, ...readRecents().filter((x) => x !== q)].slice(0, 5)
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next))
  } catch {
    /* ignore */
  }
}

// Flatten the three-category payload into a single navigable list.
// Stable order: clients → tickets → invoices, matching the section
// ordering shown in the modal.
function flatten(data: SearchResponse): SuperSearchResult[] {
  const out: SuperSearchResult[] = []
  for (const c of data.clients) {
    out.push({
      type: "client",
      id: c.id,
      title: c.name,
      subtitle: c.companyName ? `${c.companyName} · ${c.email}` : c.email,
      link: `/super/clients/${c.id}`,
    })
  }
  for (const t of data.tickets) {
    out.push({
      type: "ticket",
      id: t.id,
      title: t.subject,
      subtitle: [t.clientName, t.status, t.priority]
        .filter(Boolean)
        .join(" · "),
      // No dedicated ticket-detail route yet; the support page accepts
      // a `?ticket=<id>` query param to deep-link a row.
      link: `/super/support?ticket=${t.id}`,
    })
  }
  for (const i of data.invoices) {
    out.push({
      type: "invoice",
      id: i.id,
      title: i.invoiceNumber,
      subtitle: [i.clientName, `₹${i.amount.toLocaleString("en-IN")}`, i.status]
        .filter(Boolean)
        .join(" · "),
      link: `/super/billing?invoice=${i.id}`,
    })
  }
  return out
}

export function SuperGlobalSearch({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
}) {
  const navigate = useNavigate()
  const [q, setQ] = useState("")
  const [results, setResults] = useState<SuperSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [active, setActive] = useState(0)
  const [recents, setRecents] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<number | null>(null)

  // Reset on open + focus the input.
  useEffect(() => {
    if (open) {
      setQ("")
      setResults([])
      setActive(0)
      setRecents(readRecents())
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  // Debounced search (300 ms).
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    if (q.trim().length < 2) {
      setResults([])
      setLoading(false)
      return
    }
    setLoading(true)
    debounceRef.current = window.setTimeout(async () => {
      try {
        const { data } = await api.get("/super/search", {
          params: { q: q.trim() },
        })
        setResults(flatten(data.data as SearchResponse))
        setActive(0)
      } catch (err) {
        // Silently clearing on every error hid 401/403/500 failures
        // behind an indistinguishable "no results" state. Surface the
        // cause to devtools so future debugging is one click away.
        console.warn("[SuperGlobalSearch] request failed:", err)
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 300)
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current)
    }
  }, [q])

  const choose = useCallback(
    (r: SuperSearchResult) => {
      pushRecent(q.trim())
      onOpenChange(false)
      navigate(r.link)
    },
    [q, navigate, onOpenChange],
  )

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActive((i) => Math.min(i + 1, results.length - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActive((i) => Math.max(0, i - 1))
    } else if (e.key === "Enter") {
      e.preventDefault()
      const r = results[active]
      if (r) choose(r)
    }
  }

  // Group results by section for the rendered list.
  const groups = (() => {
    const map = new Map<string, SuperSearchResult[]>()
    for (const r of results) {
      const sec = TYPE_META[r.type].section
      if (!map.has(sec)) map.set(sec, [])
      map.get(sec)!.push(r)
    }
    return Array.from(map.entries())
  })()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* `[&>button.absolute]:hidden` suppresses the built-in shadcn
          DialogContent close button (injected at top-4 right-4). It was
          overlapping the Esc/footer-hint badge. Esc still closes via the
          Dialog's own keyboard handler. */}
      <DialogContent className="max-w-2xl gap-0 p-0 [&>button.absolute]:hidden">
        <div className="flex items-center gap-2 border-b border-[var(--color-border)] px-4 py-3">
          <Search className="h-4 w-4 text-[color:var(--color-text-muted)]" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search clients, tickets, invoices…"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-[color:var(--color-text-muted)]"
          />
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin text-[color:var(--color-text-muted)]" />
          ) : null}
        </div>

        <div className="max-h-[420px] overflow-y-auto">
          {q.trim().length < 2 ? (
            recents.length > 0 ? (
              <div className="p-2">
                <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--color-text-muted)]">
                  Recent
                </p>
                {recents.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setQ(r)}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-[color:var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]"
                  >
                    <Clock className="h-3.5 w-3.5 text-[color:var(--color-text-muted)]" />
                    {r}
                  </button>
                ))}
              </div>
            ) : (
              <p className="px-6 py-10 text-center text-sm text-[color:var(--color-text-muted)]">
                Type at least 2 characters to search.
              </p>
            )
          ) : results.length === 0 && !loading ? (
            <p className="px-6 py-10 text-center text-sm text-[color:var(--color-text-muted)]">
              No results for "{q}".
            </p>
          ) : (
            // Render each section header + its results. We compute the
            // global active-index against the flattened `results` array
            // so keyboard nav can cross section boundaries.
            <div className="py-2">
              {groups.map(([section, rs]) => (
                <div key={section} className="mb-2 last:mb-0">
                  <p className="px-4 py-1 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--color-text-muted)]">
                    {section}
                  </p>
                  {rs.map((r) => {
                    const flatIdx = results.indexOf(r)
                    const meta = TYPE_META[r.type]
                    const Icon = meta.icon
                    const isActive = flatIdx === active
                    return (
                      <button
                        key={`${r.type}-${r.id}`}
                        type="button"
                        onMouseEnter={() => setActive(flatIdx)}
                        onClick={() => choose(r)}
                        className={cn(
                          "flex w-full items-center gap-3 px-4 py-2 text-left transition",
                          isActive
                            ? "bg-[var(--color-accent-light)] text-[color:var(--color-accent-hover)]"
                            : "hover:bg-[var(--color-bg-subtle)]",
                        )}
                      >
                        <Icon className={cn("h-4 w-4 shrink-0", meta.color)} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-foreground">
                            {r.title}
                          </span>
                          <span className="block truncate text-xs text-[color:var(--color-text-muted)]">
                            {r.subtitle}
                          </span>
                        </span>
                        {isActive ? (
                          <CornerDownLeft className="h-3.5 w-3.5 text-[color:var(--color-text-muted)]" />
                        ) : null}
                      </button>
                    )
                  })}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-[var(--color-border)] px-4 py-2 text-[10px] text-[color:var(--color-text-muted)]">
          <span className="flex items-center gap-2">
            <kbd className="rounded border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-1.5 py-0.5">
              ↑↓
            </kbd>
            navigate
            <kbd className="rounded border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-1.5 py-0.5">
              Enter
            </kbd>
            open
          </span>
          <span>Super Admin search</span>
        </div>
      </DialogContent>
    </Dialog>
  )
}
